/**
 * Tasks API endpoints
 */
import { apiClient } from '../client'

export type TaskStatus = 'todo' | 'in_progress' | 'in_review' | 'done' | 'blocked' | 'cancelled'
// Single source of truth lives in ./projects (both used to export it,
// which made `export *` in index.ts ambiguous — TS2308).
import type { Priority } from './projects'

export interface Task {
  id: number
  title: string
  description: string | null
  status: TaskStatus
  priority: Priority
  project_id: number
  milestone_id: number | null
  parent_task_id: number | null
  assignee_id: number | null
  reporter_id: number
  estimated_hours: string | null
  actual_hours: string | null
  due_date: string | null
  completed_date: string | null
  position: number
  created_at: string
  updated_at: string
  metadata?: Record<string, any>
}

export interface TaskCreate {
  title: string
  description?: string
  status?: TaskStatus
  priority?: Priority
  project_id: number
  milestone_id?: number
  parent_task_id?: number
  assignee_id?: number
  assignee_ids?: number[]
  reporter_id?: number
  estimated_hours?: string
  due_date?: string
  tag_ids?: number[]
  metadata?: Record<string, any>
}

export interface TaskUpdate {
  title?: string
  description?: string
  status?: TaskStatus
  priority?: Priority
  milestone_id?: number
  assignee_id?: number
  assignee_ids?: number[]
  estimated_hours?: string
  actual_hours?: string
  due_date?: string
  tag_ids?: number[]
  metadata?: Record<string, any>
}

export const tasksApi = {
  /**
   * List tasks
   */
  async list(params?: {
    project_id?: number
    milestone_id?: number
    assignee_id?: number
    status?: string
    priority?: string
    skip?: number
    limit?: number
    include_deleted?: boolean
  }): Promise<Task[]> {
    const response = await apiClient.get<Task[]>('/api/v1/tasks', { params })
    return response.data
  },

  /**
   * Get task by ID
   */
  async get(id: number): Promise<Task> {
    const response = await apiClient.get<Task>(`/api/v1/tasks/${id}`)
    return response.data
  },

  /**
   * Create task
   */
  async create(data: TaskCreate): Promise<Task> {
    const response = await apiClient.post<Task>('/api/v1/tasks', data)
    return response.data
  },

  /**
   * Update task
   */
  async update(id: number, data: TaskUpdate): Promise<Task> {
    const response = await apiClient.put<Task>(`/api/v1/tasks/${id}`, data)
    return response.data
  },

  /**
   * Delete task
   */
  async delete(id: number): Promise<void> {
    await apiClient.delete(`/api/v1/tasks/${id}`)
  },

  /**
   * Assign task to users
   */
  async assign(id: number, user_ids: number[]): Promise<Task> {
    const response = await apiClient.post<Task>(`/api/v1/tasks/${id}/assign`, user_ids)
    return response.data
  },

  /**
   * Get subtasks
   */
  async getSubtasks(id: number): Promise<Task[]> {
    const response = await apiClient.get<Task[]>(`/api/v1/tasks/${id}/subtasks`)
    return response.data
  },

  /**
   * Restore a soft-deleted task (superuser only)
   */
  async restore(id: number): Promise<Task> {
    const response = await apiClient.post<Task>(`/api/v1/tasks/${id}/restore`)
    return response.data
  },
}

